import { Outlet, useNavigate } from "react-router-dom";
import UserActionsMenu from "~/components/login/UserActionsMenu";
import SosAlertsIndicator from "~/components/command/SosAlertsIndicator";
import VirtualVisitChime from "~/components/command/VirtualVisitChime";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DemoScenariosDialog } from "~/components/demo/DemoScenariosDialog";
import { RyansScenariosDialog } from "~/components/demo/RyansScenariosDialog";
import { HomeHealthDemoAppBarButton, HomeHealthDemoRoot } from "~/features/home-health-demo";
import { useApolloClient } from "@apollo/client";

import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Menu,
  Toolbar,
  Typography,
  Button,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";

import "./assets";
import MainMenu from "~/components/MainMenu";
import PatientSearchBox from "../patient/PatientSearchBox";
import { useAuth } from "~/state/auth";
import { useHasPermission, Permission } from "~/hooks/permissions";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import CenteredBox from "../common/CenteredBox";
import InactivityTimeoutPrompt from "~/components/common/InactivityTimeoutPrompt";
import NoCommandCenterPermission from "~/components/login/NoCommandCenterPermission";
import { useEffect, useState } from "react";
import SignOutScreen from "../login/SignOutScreen";
import { useOrganization } from "~/providers/GraphqlProvider";
import { useSearchParams } from "react-router-dom";
import { IDP_QUERY_PARAM } from "~/state/auth/constants";
import { clearAllDemoState } from "~/services/demo/demoStateManager";

export const AuthenticatedRoot: React.FC = () => {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { isAuthenticated, setLoginRedirectPath, isLoggingOut } = useAuth();
  const canUseCommandCenter = useHasPermission(Permission.useWebApp);
  const { organizationName } = useOrganization();
  const [searchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState<null | HTMLElement>(null)
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  // Ryan's Scenarios is currently hidden from the app bar (no trigger button).
  // Kept mounted so it can be re-enabled by wiring up a button to setRyansDialogOpen.
  const [ryansDialogOpen, setRyansDialogOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated === false && !isLoggingOut) {
      const path = window.location.pathname + window.location.search;
      if (path !== "/") {
        setLoginRedirectPath(path);
      }
      const queryIdp = searchParams.get(IDP_QUERY_PARAM);
      navigate(`/login${queryIdp ? `?${IDP_QUERY_PARAM}=${queryIdp}` : ""}`, { replace: true });
    }
  }, [isAuthenticated, isLoggingOut]);

  // Clear demo state on page refresh (but not on navigation)
  useEffect(() => {
    // Only clear demo state on page refresh, not when navigating to demo routes
    const handleBeforeUnload = () => {
      // This runs when the page is actually being unloaded/refreshed
      // Mark that we should clear state on next load
      sessionStorage.setItem('should-clear-demo', 'true');
    };

    // Check if we should clear state (set by previous page unload)
    const shouldClear = sessionStorage.getItem('should-clear-demo') === 'true';
    if (shouldClear) {
      clearAllDemoState();
      console.log('%c[DEMO] Demo state cleared on page refresh', 'color: #ff9800; font-weight: bold;');
      sessionStorage.removeItem('should-clear-demo');

      // Refetch admission-related queries to ensure fresh data from server
      apolloClient.refetchQueries({
        include: ['CountUnscheduledAdmissions', 'ListActivePatients'],
      }).then(() => {
        console.log('%c[DEMO] Refetched queries after demo state clear', 'color: #ff9800; font-weight: bold;');
      }).catch((error) => {
        console.error('[DEMO] Failed to refetch queries:', error);
      });
    }

    // Listen for page unload
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [apolloClient]);

  if (isLoggingOut) {
    return (
      <SignOutScreen />
    )
  }

  if (!isAuthenticated || canUseCommandCenter === undefined) {
    return (
      <CenteredBox>
        <HourglassBottomIcon fontSize="large" sx={{ textAlign: "center" }} />
      </CenteredBox>
    );
  }

  if (!canUseCommandCenter) {
    return <NoCommandCenterPermission />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          width: "100vw",
          alignItems: "stretch",
        }}
      >
        <AppBar
          position="fixed"
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <Toolbar
            sx={{
              pr: "24px", // keep right padding when drawer closed
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton sx={{ mr: 2 }} onClick={e => setMenuOpen(e.currentTarget)} title="Application Menu">
                <MenuIcon sx={{ color: "var(--AppBar-color)" }} />
              </IconButton>
              <Menu
                open={!!menuOpen}
                anchorEl={menuOpen}
                onClose={() => setMenuOpen(null)}
              >
                <MainMenu closeMenu={() => setMenuOpen(null)} />
              </Menu>
              <Typography
                component="h1"
                variant="h6"
                color="inherit"
                noWrap
                sx={{ flexGrow: 1 }}
              >
                MedArrive Platform
              </Typography>
              <Box sx={{ ml: 5 }}>
                <PatientSearchBox onPatientClicked={(patient) => {
                  navigate(`/patients/${patient?.id}`);
                }} />
              </Box>
            </Box>
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ScienceIcon />}
                onClick={() => setDemoDialogOpen(true)}
                sx={{
                  borderColor: "rgba(255, 255, 255, 0.5)",
                  "&:hover": {
                    borderColor: "rgba(255, 255, 255, 0.8)",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Demo Scenarios
              </Button>
              <HomeHealthDemoAppBarButton />
              <SosAlertsIndicator />
              <UserActionsMenu />
            </Box>
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === "light"
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            height: "100%",
            width: "100%",
            alignItems: "stretch",
            overflow: "auto",
          }}
        >
          {/* Phantom Toolbar used for padding */}
          <Toolbar sx={{ visibility: "hidden" }} />
          <Container maxWidth={false}>
            <Outlet />
          </Container>
        </Box>
      </Box>

      <InactivityTimeoutPrompt />
      <VirtualVisitChime />
      <DemoScenariosDialog
        open={demoDialogOpen}
        onClose={() => setDemoDialogOpen(false)}
      />
      {/* Hidden from the app bar for now; mounted so it can be re-enabled later. */}
      <RyansScenariosDialog
        open={ryansDialogOpen}
        onClose={() => setRyansDialogOpen(false)}
      />
      <HomeHealthDemoRoot />
    </LocalizationProvider>
  );
};
